import { useState, useEffect } from 'react'
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Grid from '@mui/material/Unstable_Grid2';
import FormHelperText from '@mui/material/FormHelperText';

import { User } from '../../types/UserTypes';
import { BVTConstants } from '../../../utils/BVTConstants';
import axios from '../../../services/ApiService';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../support/LoadingSpinner';

export function UpdateProfile(): React.JSX.Element {
  const [userData, setUserData] = useState<User>({
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
    contact: '',
    status: '',
    role: '',
    profilePictureId: 0,
    token: '',
    createdAt: '',
    updatedAt: '',
    lastLogin: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    contact: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/getCurrentUser");
      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching user data.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const newErrors = {
      firstName: '',
      lastName: '',
      contact: ''
    };

    if (!userData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (userData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!userData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (userData.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!userData.contact.trim()) {
      newErrors.contact = 'Phone number is required';
    } else {
      if (!BVTConstants.PHONE_VALIDATION_PATTERN.test(userData.contact.trim())) {
        newErrors.contact = 'Please enter a valid phone number';
      }
      if (userData.contact.trim().length < BVTConstants.PHONE_MIN_LENGTH || 
          userData.contact.trim().length > BVTConstants.PHONE_MAX_LENGTH) {
        newErrors.contact = `Phone number must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} characters`;
      }
    }

    if (Object.values(newErrors).some(error => error !== '')) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/updateUserDetails", userData);
      toast.success("Profile Updated Successfully!");
      setUserData(response.data);
      setErrors({
        firstName: '',
        lastName: '',
        contact: ''
      });
    } catch (error) {
      console.error('Error updating profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader subheader={userData?.email} title="Profile" />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required error={!!errors.firstName}>
                <InputLabel>First name</InputLabel>
                <OutlinedInput 
                  label="First name" 
                  name="firstName"
                  value={userData.firstName}
                  inputProps={{ 
                    maxLength: 20,
                  }}
                  onChange={(e) => setUserData({...userData, firstName: e.target.value})}
                />
                {errors.firstName && (
                  <FormHelperText error>{errors.firstName}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required error={!!errors.lastName}>
                <InputLabel>Last name</InputLabel>
                <OutlinedInput 
                  label="Last name" 
                  name="lastName"
                  value={userData.lastName}
                  inputProps={{ 
                    maxLength: 15,
                  }}
                  onChange={(e) => setUserData({...userData, lastName: e.target.value})}
                />
                {errors.lastName && (
                  <FormHelperText error>{errors.lastName}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid md={12} xs={12}>
              <FormControl fullWidth required error={!!errors.contact}>
                <InputLabel>Phone number</InputLabel>
                <OutlinedInput 
                  label="Phone number" 
                  name="contact"
                  type="tel"
                  value={userData.contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, '');
                    setUserData({...userData, contact: value});
                    if (errors.contact) {
                      setErrors(prev => ({ ...prev, contact: '' }));
                    }
                  }}
                  inputProps={{ 
                    maxLength: BVTConstants.PHONE_MAX_LENGTH,
                  }}
                  placeholder="10-15 digits"
                />
                {errors.contact && (
                  <FormHelperText error>{errors.contact}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button variant="contained" type="submit">Update</Button>
        </CardActions>
      </Card>
      <LoadingSpinner loading={loading} />
    </form>
  );
}
